<script setup lang="ts">
import { computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import EntityLink from '@/components/base/EntityLink.vue';
import { getPepMotiveLabel } from '@/constants/bdcPepMotive';
import { isRecord, toNullableBoolean, toNullableString } from '@/utils/coerce';
import { formatDate } from '@/utils/formatters';
import type { KycCategory } from '@/types/kyc';

interface RelatedPepRow {
  relatedPepTaxId: string | null;
  relatedPepName: string | null;
  relationship: string | null;
  isActive: boolean | null;
  startDate: string | null;
  endDate: string | null;
  department: string | null;
  jobTitle: string | null;
}

const props = defineProps<{ category: KycCategory }>();

/** `data_json` is a flat array from the pipeline: one row per position held by a related PEP. */
const relatedPeps = computed<RelatedPepRow[]>(() => {
  const raw: unknown = props.category.data_json;
  if (!Array.isArray(raw)) return [];

  return raw.filter(isRecord).map((item) => ({
    relatedPepTaxId: toNullableString(item.related_pep_tax_id),
    relatedPepName: toNullableString(item.name),
    relationship: getPepMotiveLabel(toNullableString(item.relationship)),
    isActive: toNullableBoolean(item.is_active),
    startDate: toNullableString(item.start_date),
    endDate: toNullableString(item.end_date),
    department: toNullableString(item.department),
    jobTitle: toNullableString(item.job_title),
  }));
});
</script>

<template>
  <div class="mt-3 w-full min-w-0 max-w-full [contain:inline-size]">
    <DataTable :value="relatedPeps" scrollable scroll-height="24rem" class="w-full max-w-full text-xs">
      <template #empty>
        <div class="p-6 text-center text-xs text-surface-500">Nenhum PEP relacionado encontrado nesta análise.</div>
      </template>

      <Column field="relatedPepTaxId" header="CPF do PEP" style="min-width: 8rem">
        <template #body="{ data: row }: { data: RelatedPepRow }">
          <EntityLink v-if="row.relatedPepTaxId" :tax-id="row.relatedPepTaxId" type="person" :show-icon="false" size="compact" />
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column field="relatedPepName" header="Nome do PEP" sortable style="min-width: 10rem">
        <template #body="{ data: row }: { data: RelatedPepRow }">
          <span class="font-medium text-surface-900">{{ row.relatedPepName || '—' }}</span>
        </template>
      </Column>
      <Column field="relationship" header="Relacionamento" sortable style="min-width: 8rem">
        <template #body="{ data: row }: { data: RelatedPepRow }">
          <span class="text-surface-700">{{ row.relationship || '—' }}</span>
        </template>
      </Column>
      <Column field="isActive" header="Status" sortable style="min-width: 6rem">
        <template #body="{ data: row }: { data: RelatedPepRow }">
          <Tag
            v-if="row.isActive !== null"
            :value="row.isActive ? 'Ativo' : 'Inativo'"
            :severity="row.isActive ? 'danger' : 'secondary'"
            class="!py-0 !px-1.5 !text-[10px]"
          />
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column field="startDate" header="Início" sortable style="min-width: 6.5rem">
        <template #body="{ data: row }: { data: RelatedPepRow }">{{ formatDate(row.startDate, undefined, '—') }}</template>
      </Column>
      <Column field="endDate" header="Fim" sortable style="min-width: 6.5rem">
        <template #body="{ data: row }: { data: RelatedPepRow }">{{ formatDate(row.endDate, undefined, '—') }}</template>
      </Column>
      <Column field="department" header="Local de Trabalho" style="min-width: 10rem; max-width: 16rem">
        <template #body="{ data: row }: { data: RelatedPepRow }">
          <span :title="row.department || ''" class="block text-surface-700">{{ row.department || '—' }}</span>
        </template>
      </Column>
      <Column field="jobTitle" header="Título do Cargo" style="min-width: 10rem; max-width: 16rem">
        <template #body="{ data: row }: { data: RelatedPepRow }">
          <span :title="row.jobTitle || ''" class="block text-surface-700">{{ row.jobTitle || '—' }}</span>
        </template>
      </Column>
    </DataTable>
  </div>
</template>
